import React, { useMemo, useState } from 'react';
import type { GridApi } from 'ag-grid-community';
import styled from 'styled-components';

import GridCsvExportRegistrar from 'components/Grid/ExportButton/GridCsvExportRegistrar';
import PaceMatrixContent from 'components/PaceMatrix/PaceMatrixContent';
import SingleMetricDateMatrixContent from 'components/SingleMetricDateMatrix/SingleMetricDateMatrixContent';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import CardContext from 'contexts/CardContext';
import ComparisonContext from 'contexts/ComparisonContext';
import isPaceMatrix from 'types/visTypeCheckers/isPaceMatrix';

import {
  usePublishBlockExportHandle,
  usePublishBlockGridApi,
} from './BlockGridApiContext';
import buildLegacyMatrixDefinition from './buildLegacyMatrixDefinition';
import ChartPlaceholder from './ChartPlaceholder';
import useRenderedComparison from './useRenderedComparison';

// Fills the block, as a chart does. Tables in unselected blocks ignore the
// pointer, like charts (StudioChart).
const MatrixBody = styled.div<{ $isInteractive: boolean }>`
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 0;

  > div {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    pointer-events: ${(props) => (props.$isInteractive ? 'auto' : 'none')};
  }
`;

// A date comparison or pace Table (a block, or a Composite's element): drawn
// by the legacy Single Metric Date Matrix or Pace Matrix it saves as, within
// its dates and filters, against its baseline window (or the report's
// comparison). Stable while its settings don't change, so it doesn't
// refetch on a move. w and h are the grid size of the block it sits in.
const StudioLegacyMatrix = ({
  content,
  name,
  w,
  h,
  isInteractive,
  isOnView,
}: {
  content: Studio.ChartContent;
  name: string;
  w: number;
  h: number;
  isInteractive: boolean;
  isOnView: boolean;
}) => {
  const [gridApi, setGridApi] = useState<GridApi>();
  // In place of the gridApi when DataTable draws the matrix
  const [exportHandle, setExportHandle] = useState<DataTable.ExportHandle>();
  // Exported from the block's (or element's) menu
  usePublishBlockGridApi(gridApi);
  usePublishBlockExportHandle(exportHandle);
  const settingsKey = JSON.stringify([
    content.metricIds,
    content.description,
    content.dimensions,
    content.dimensionOptions,
    content.table,
  ]);
  const built = useMemo(
    () => {
      const [metricIds, description, dimensions, dimensionOptions, table] =
        JSON.parse(settingsKey);
      return buildLegacyMatrixDefinition({
        card: {
          visType: 'table',
          metricIds,
          description,
          dimensions,
          dimensionOptions,
          table,
        },
        id: content.id,
        name,
      });
    },
    // The settings are compared by value
    [content.id, name, settingsKey],
  );
  const comparison = useRenderedComparison({ content, isOnView });
  const comparisonContextValue = useMemo(
    () => ({ currentComparison: comparison }),
    [comparison],
  );
  const matrix = 'chartDefinition' in built ? built.chartDefinition : undefined;
  const cardContextValue = useMemo(
    () => ({ i: content.id, w, h, chartDefinition: matrix }),
    [content.id, h, matrix, w],
  );

  if (!matrix) {
    return (
      <ChartPlaceholder
        cardId={content.id}
        visType="table"
        message={'missing' in built ? built.missing : ''}
      />
    );
  }
  return (
    <MatrixBody
      $isInteractive={isInteractive}
      data-testid={`studio-card-legacy-matrix-${content.id}`}
    >
      <div>
        <CardContext.Provider value={cardContextValue}>
          <ComparisonContext.Provider value={comparisonContextValue}>
            <GqlClientProvider
              visName={matrix.name}
              visId={matrix.id}
              visType={matrix.type}
            >
              <>
                {/* The scheduled report's CSV, as the matrix gadgets' own top
                  bars register it. No-op unless isPdfExport. */}
                {(gridApi || exportHandle) && (
                  <GridCsvExportRegistrar
                    overrideGridApi={gridApi}
                    exportHandle={exportHandle}
                    exportName={name}
                  />
                )}
                {isPaceMatrix(matrix) ? (
                  <PaceMatrixContent
                    gadget={matrix}
                    gridApi={gridApi}
                    setGridApi={setGridApi}
                    setExportHandle={setExportHandle}
                  />
                ) : (
                  <SingleMetricDateMatrixContent
                    gadget={matrix}
                    gridApi={gridApi}
                    setGridApi={setGridApi}
                    setExportHandle={setExportHandle}
                  />
                )}
              </>
            </GqlClientProvider>
          </ComparisonContext.Provider>
        </CardContext.Provider>
      </div>
    </MatrixBody>
  );
};

export default StudioLegacyMatrix;
